function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
/**
 * Factory function to create type-safe store objects
 */ function createStore(definition) {
    return _extends({}, definition, {
        _get_value (value) {
            if (value === null && definition.defaultValue !== undefined) {
                // If no value found but we have a default, return the default
                return definition.defaultValue;
            }
            if (value === null) {
                return null;
            }
            // If no validator, just return the value as-is
            if (!definition.validator) {
                return value;
            }
            try {
                return definition.validator.parse(value);
            } catch (error) {
                console.error(`Invalid schema for ${definition.key}:`, error);
                return null;
            }
        },
        get (provider) {
            const value = provider.get(definition.storageType, definition.key);
            return this._get_value(value);
        },
        getAsync (provider) {
            return _async_to_generator(function*() {
                return provider.get(definition.storageType, definition.key).then((value)=>this._get_value(value));
            }).apply(this);
        },
        _set_value (value) {
            // If no validator, just return the value as-is
            if (!definition.validator) {
                return value;
            }
            try {
                return definition.validator.parse(value);
            } catch (error) {
                throw new Error(`Failed to store value for ${definition.key}: ${error}`);
            }
        },
        set (provider, value) {
            const validatedValue = this._set_value(value);
            provider.set(definition.storageType, definition.key, validatedValue);
        },
        setAsync (provider, value) {
            return _async_to_generator(function*() {
                const validatedValue = this._set_value(value);
                yield provider.set(definition.storageType, definition.key, validatedValue);
            }).apply(this);
        },
        remove (provider) {
            provider.remove(definition.storageType, definition.key);
        },
        removeAsync (provider) {
            return _async_to_generator(function*() {
                yield provider.remove(definition.storageType, definition.key);
            })();
        },
        exists (provider) {
            return provider.has(definition.storageType, definition.key);
        },
        existsAsync (provider) {
            return _async_to_generator(function*() {
                return provider.has(definition.storageType, definition.key);
            })();
        }
    });
}

/**
 * Browser implementation of StorageProvider using localStorage and sessionStorage
 */ class BrowserStorageProvider {
    /**
   * Get a value from browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   * @returns The value or null if not found
   */ get(type, key) {
        if (typeof window === "undefined") {
            console.error(`${type}Storage is not available in this environment`);
            return null;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return null;
            const item = storage.getItem(key);
            if (item === null) {
                return null;
            }
            return JSON.parse(item);
        } catch (error) {
            console.error(`Failed to get item from ${type}Storage: ${error}`);
            return null;
        }
    }
    /**
   * Set a value in browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   * @param value - Value to store
   */ set(type, key, value) {
        if (typeof window === "undefined") {
            return;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return;
            const serializedValue = JSON.stringify(value);
            storage.setItem(key, serializedValue);
        } catch (error) {
            console.error(`Failed to set item in ${type}Storage: ${error}`);
        }
    }
    /**
   * Remove a value from browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   */ remove(type, key) {
        if (typeof window === "undefined") {
            return;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return;
            storage.removeItem(key);
        } catch (error) {
            console.error(`Failed to remove item from ${type}Storage: ${error}`);
        }
    }
    /**
   * Clear all values from browser storage
   * @param type - Storage type (local, session, cookie)
   */ clear(type) {
        if (typeof window === "undefined") {
            return;
        }
        try {
            const storage = this.getStorageByType(type);
            if (!storage) return;
            storage.clear();
        } catch (error) {
            console.error(`Failed to clear ${type}Storage: ${error}`);
        }
    }
    /**
   * Check if a key exists in browser storage
   * @param type - Storage type (local, session, cookie)
   * @param key - Storage key
   * @returns True if the key exists
   */ has(type, key) {
        if (typeof window === "undefined") {
            return false;
        }
        const storage = this.getStorageByType(type);
        if (!storage) return false;
        return storage.getItem(key) !== null;
    }
    /**
   * Get the appropriate storage object based on type
   * @param type - Storage type (local, session, cookie)
   * @returns The storage object
   */ getStorageByType(type) {
        if (typeof window === "undefined") {
            return null;
        }
        switch(type){
            case "local":
                return window.localStorage;
            case "session":
                return window.sessionStorage;
            case "cookie":
                console.warn("Cookie storage not yet implemented in BrowserStorageProvider");
                return null;
            default:
                return null;
        }
    }
    constructor(){
        this.type = "sync";
        if (typeof window === "undefined") {
            throw new Error("BrowserStorageProvider is not available in this environment");
        }
    }
}

export { BrowserStorageProvider, createStore };
